import dayjs from 'dayjs';
import type { Dashboard } from '../contract';

// Turns the numbers the API sends for the business dashboard into what the dashboard shows. Pure, so it can be tested without a
// screen.

// (Optional fields: the compile-time types zod infers are loose in this project; the server always sends both.)
type Change = { current?: number, previous?: number };

// part / whole as a whole percentage; null when there is no whole.
export const percentOf = (part: number, whole: number): number | null =>
    whole === 0 ? null : Math.round((part / whole) * 100);

export const formatNumber = (value: number) => value.toLocaleString('en-US');

// How this period compares with the one before it, in words, with an arrow that is also a word for those who can't see it.
// No judgement is made in colour: more is not always better (more summaries written is also more money).
export function describeChange({ current = 0, previous = 0 }: Change, days: number): string {
    const before = `the previous ${days} days`;
    if (current === 0 && previous === 0) return `none in either period`;
    if (previous === 0) return `▲ up from none in ${before}`;
    if (current === previous) return `same as ${before}`;

    const share = Math.round((Math.abs(current - previous) / previous) * 100);
    return `${current > previous ? '▲ up' : '▼ down'} ${share}% from ${formatNumber(previous)} in ${before}`;
}

// "80%" (4 of 5), or a dash when nothing had a due time to be on time for.
export function describeOnTime({ onTime, total }: Dashboard['onTime']) {
    const share = percentOf(onTime, total);
    return { value: share === null ? '–' : `${share}%`, caption: total === 0 ? 'no completed tasks had a due time' : `${formatNumber(onTime)} of ${formatNumber(total)} completed tasks were done by their due time` };
}

// The steps of getting started, each as a share of the people who signed up.
export function activationSteps(activation: Dashboard['activation']) {
    return [
        { label: 'Signed up', people: activation.signedUp },
        { label: 'Created a board', people: activation.createdBoard },
        { label: 'Created a task', people: activation.createdTask },
        { label: 'Completed a task', people: activation.completedTask },
        { label: 'Played a summary', people: activation.playedSummary },
    ].map((step) => ({ ...step, share: percentOf(step.people, activation.signedUp) }));
}

// "Oct 7" for a day like "2026-10-07" (a calendar day, so no time zone is involved).
export const dayLabel = (day: string) => dayjs(day).format('MMM D');

// How far along the daily limit the busiest day got, so a line for the limit is only drawn when it matters.
export const nearLimit = (daily: Dashboard['daily'], limit: number | null) =>
    limit !== null && Math.max(0, ...daily.map((d) => d.summariesWritten)) >= limit / 2;

export const eventLabel = (eventType: string) => eventType.replace(/[._]/g, ' ');

// "5 minutes ago" is avoided on purpose: the page may stay open; a time is exact.
export const lastSeenLabel = (lastSeen: string | null) => (lastSeen ? dayjs(lastSeen).format('MMM D, h:mm A') : '–');
